import React, { useEffect } from 'react';
import { gsap } from 'gsap';

//Components
import useObserver from './useObserver';

//Icons
import iconExperienced from '../images/icon-experienced.png';
import iconObsessed from '../images/icon-obsessed.png';
import iconClock from '../images/icon-clock.png';
import iconPrivacy from '../images/icon-privacy.png';

const SectionFeatures = ({ lang }) => {
  const [ref, setRef, animate] = useObserver({
    init: true,
    root: null,
    rootMargin: `-20% 0% -20% 0%`,
  });

  const renderTexts = language => {
    switch (language) {
      case 'en':
        return [
          {
            title: 'Experienced team',
            description:
              'Our team of manufacturing experts have over 20 years of experience in the field.',
          },
          {
            title: 'Obsessed with quality',
            description:
              'The high quality of our production is achieved through strict internal quality control.',
          },
          {
            title: 'On-time delivery',
            description:
              'We stick to our deadlines and take on projects that would fit well with our production availability.',
          },
          {
            title: 'Privacy and security',
            description:
              'Confidentiality is a must for us and we have taken all measured to ensure that all our projects and information are safe.',
          },
        ];
      case 'de':
        return [
          {
            title: 'Erfahrenes Team',
            description:
              'Unser Team von Fertigungsexperten verfügt über mehr als 20 Jahre Erfahrung auf diesem Gebiet.',
          },
          {
            title: 'Besessen von Qualität',
            description:
              'Die hohe Qualität unserer Produktion wird durch eine strenge interne Qualitätskontrolle erreicht.',
          },
          {
            title: 'Immer pünktlich',
            description:
              'Wir halten unsere Liefertermine immer ein und begleiten nur Projekte, welche wir gemäss unserer Produktionsverfügbarkeit garantiert umsetzen können.',
          },
          {
            title: 'Privatsphäre und Sicherheit',
            description:
              'Vertraulichkeit ist für uns ein Muss, und wir haben weitreichende Maßnahmen ergriffen, um sicherzustellen, dass alle unsere Projekte und Informationen sicher sind.',
          },
        ];
      case 'bg':
        return [
          {
            title: 'Екип от експерти',
            description:
              'Нашият екип от висококвалифицирани специалисти има над 20 години опит в тази област.',
          },
          {
            title: 'Водени от качеството',
            description:
              'Високото качество на нашата продукция е гарантирано от постоянния контрол на всеки етап от производството.',
          },
          {
            title: 'Придържане към сроковете',
            description:
              'Спазваме крайните срокове и се стараем да напаснем графика си според вашите нужди.',
          },
          {
            title: 'Сигурност на информацията',
            description:
              'Конфиденциалността на всичката ни вътрешна и външна комуникация е задължителна за нас.',
          },
        ];
      default:
        return [
          {
            title: '',
            description: '',
          },
          {
            title: '',
            description: '',
          },
          {
            title: '',
            description: '',
          },
          {
            title: '',
            description: '',
          },
        ];
    }
  };

  useEffect(() => {
    if (ref && window.innerWidth > 767) {
      gsap.set(ref, {
        yPercent: 30,
        opacity: 0,
        force3D: true,
      });
    }
  }, [ref]);

  useEffect(() => {
    if (animate && window.innerWidth > 767) {
      gsap.to(ref, {
        duration: 1,
        ease: 'power4.out',
        yPercent: 0,
        opacity: 1,
        force3D: true,
      });
    }
  }, [animate]);

  return (
    <section ref={setRef} className="section-features py-7 py-xl-10 py-mg-17">
      <div className="container px-0 px-md-5">
        <div className="bg-black pt-12 pb-7 px-5 pt-xl-10 px-xl-10 pt-lg-10 pt-mg-17 px-lg-7 px-mg-17 pb-mg-5">
          <div className="row justify-content-between">
            <div className="col-md-6 col-mg-5 mb-7 mb-md-8 mb-lg-12">
              <div className="d-flex align-items-start">
                <div
                  className="position-relative mr-5"
                  style={{
                    width: '9.6rem',
                    height: '9.6rem',
                    flex: '1 0 9.6rem',
                    border: '1px solid rgba(123,135,141,0.4)',
                  }}
                >
                  <img
                    className="img-fluid position-absolute"
                    src={iconExperienced}
                    alt={renderTexts(lang)[0].title}
                    style={{
                      width: 'calc(100% - 3.2rem)',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </div>

                <div>
                  <h4 className="text-headline text-white mb-3">
                    {renderTexts(lang)[0].title}
                  </h4>

                  <p className="text-grey text-small">
                    {renderTexts(lang)[0].description}
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-mg-5 mb-7 mb-md-8 mb-lg-12">
              <div className="d-flex align-items-start">
                <div
                  className="position-relative mr-5"
                  style={{
                    width: '9.6rem',
                    height: '9.6rem',
                    flex: '1 0 9.6rem',
                    border: '1px solid rgba(123,135,141,0.4)',
                  }}
                >
                  <img
                    className="img-fluid position-absolute"
                    src={iconObsessed}
                    alt={renderTexts(lang)[1].title}
                    style={{
                      width: 'calc(100% - 3.2rem)',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </div>

                <div>
                  <h4 className="text-headline text-white mb-3">
                    {renderTexts(lang)[1].title}
                  </h4>

                  <p className="text-grey text-small">
                    {renderTexts(lang)[1].description}
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-mg-5 mb-7 mb-md-8 mb-lg-12">
              <div className="d-flex align-items-start">
                <div
                  className="position-relative mr-5"
                  style={{
                    width: '9.6rem',
                    height: '9.6rem',
                    flex: '1 0 9.6rem',
                    border: '1px solid rgba(123,135,141,0.4)',
                  }}
                >
                  <img
                    className="img-fluid position-absolute"
                    src={iconClock}
                    alt={renderTexts(lang)[2].title}
                    style={{
                      width: 'calc(100% - 3.2rem)',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </div>

                <div>
                  <h4 className="text-headline text-white mb-3">
                    {renderTexts(lang)[2].title}
                  </h4>

                  <p className="text-grey text-small">
                    {renderTexts(lang)[2].description}
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-mg-5 mb-7 mb-md-8 mb-lg-12">
              <div className="d-flex align-items-start">
                <div
                  className="position-relative mr-5"
                  style={{
                    width: '9.6rem',
                    height: '9.6rem',
                    flex: '1 0 9.6rem',
                    border: '1px solid rgba(123,135,141,0.4)',
                  }}
                >
                  <img
                    className="img-fluid position-absolute"
                    src={iconPrivacy}
                    alt={renderTexts(lang)[3].title}
                    style={{
                      width: 'calc(100% - 3.2rem)',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </div>

                <div>
                  <h4 className="text-headline text-white mb-3">
                    {renderTexts(lang)[3].title}
                  </h4>

                  <p className="text-grey text-small">
                    {renderTexts(lang)[3].description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectionFeatures;
